/**
 * The contacts list. CRM-BUILD item 27, wave 5.
 *
 * ┌─ ⛔ THREE ABSENCES, THREE SENTENCES, AND THIS TIME BEFORE THE INSPECTION ───┐
 * │ `a-surface-says-nothing-about-what-it-lacks` was found on the contact record │
 * │ by Amir looking at it. It is applied here before anyone looks:               │
 * │                                                                            │
 * │   no contacts at all      a fresh deployment, nothing written or imported   │
 * │   no contacts match       a filter IS applied and matched none              │
 * │   none you can see        they exist and this agent's scope excludes them   │
 * │                                                                            │
 * │ ⛔ AND A FOURTH THAT IS NOT AN ABSENCE: blocked contacts are missing BY      │
 * │ RULE. §4.6 rejected contact-only blocking because hiding something with     │
 * │ nothing saying so is a surface that lies by omission - so the list says how  │
 * │ many it hid and offers the view that holds them.                           │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ SPACING, COLOUR AND TYPOGRAPHY ARE VISUAL-DESIGN'S, which runs after
 * CRM-BUILD closes. Built against tokens so that pass has one system to work on.
 */
import { useEffect, useRef, useState } from 'react'
import {
  createSavedSearch,
  deleteSavedSearch,
  fetchContactsPage,
  fetchSavedSearches,
  setContactBlocked,
  saveListColumns,
  type ContactListRow,
  type ContactsPageResponse,
  type ResolvedColumn,
  type SavedSearch,
} from '../data/contacts-api.js'
import { ROWS_PER_PAGE, parseRowsPerPage } from '../../../src/services/list-paging.js'
import { formatMoney } from '../data/money.js'
import './contact-record.css'
import './contacts-list.css'

/**
 * ⛔ THE COLUMN LITERAL IS GONE AND THE SERVER OWNS THE VOCABULARY. Amir
 * 2026-09-12: every list view lets a person choose its columns, fixed fields and
 * custom fields both. The set arrives resolved - id, label, sort, visible - so
 * the account's `value_label` and a custom field's label reach the header by the
 * same path, and neither is spelled twice.
 *
 * ⚠️ WHAT STAYS ON THIS SIDE IS HOW A CELL IS DRAWN, which is code rather than a
 * constant: the name cell carries the number and the blocked marker, the value
 * cell needs the currency. `cellFor` below is that switch and nothing else.
 */

/**
 * ⛔ `Intl.NumberFormat` RATHER THAN `toLocaleString`, AND A CHECK DECIDED IT.
 * The first version wrote `total.toLocaleString()` and
 * `ops/check-timezone-resolution.sh` failed: it greps for `toLocaleString`
 * because `Date.prototype.toLocaleString` is the call that silently formats in
 * the wrong zone, and no grep can tell a Date's from a Number's.
 *
 * ⚠️ NARROWING THE CHECK WAS THE WRONG FIX. The token really is ambiguous, so a
 * pattern that let this through would let the Date case through too. Using the
 * unambiguous formatter costs one line and keeps the check strict.
 */
const COUNT_FORMAT = new Intl.NumberFormat()

/**
 * ⛔ SAVED SEARCHES, WHICH ARE SAVED VIEWS. Amir asked for a button that saves
 * the search somebody just built; `Build_Order:494` asks for saved views as
 * *"first-class sidebar items rather than a saved-filter dropdown"*. **They are
 * one feature with two entry points** - the button creates one, the bar is where
 * it lives - and building them separately produces the dropdown the plan
 * rejected.
 *
 * ⛔ RESTORING ONE PUTS ITS PARAMETERS BACK IN THE URL AND NOTHING ELSE. The
 * list then filters exactly as it does for a hand-typed link, so there is no
 * second filtering path. The server stores the same parameters as a segment
 * definition, and `tests/saved-searches.test.ts` compares the two answers row
 * for row.
 *
 * ⚠️ WHAT IS NOT SAVED: the page, the rows per page, and the columns. The first
 * two are where somebody WAS rather than what they were looking for. The
 * columns are per person, and a shared view carrying a column set would impose
 * one person's columns on everybody - the visibility question again, and it is
 * COLLABORATION's.
 */
function SavedSearches({
  params,
  onParamsChange,
}: {
  params: URLSearchParams
  onParamsChange: (next: URLSearchParams) => void
}) {
  const [searches, setSearches] = useState<SavedSearch[] | null>(null)
  const [label, setLabel] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const reload = () => {
    void fetchSavedSearches('contacts')
      .then(setSearches)
      .catch(() => setSearches([]))
  }
  useEffect(reload, [])

  /**
   * ⛔ WHAT WOULD BE SAVED, COMPUTED HERE SO THE BUTTON CAN SAY WHETHER THERE IS
   * ANYTHING. The server refuses a search that narrows nothing - that is the
   * authority - but a Save button that is always live and sometimes answers "a
   * saved search needs a filter" teaches people to ignore it.
   */
  const savable = new URLSearchParams()
  for (const key of ['q', 'country', 'view', 'sort', 'dir']) {
    const value = params.get(key)
    if (value !== null && value !== '') savable.set(key, value)
  }
  const narrows =
    (savable.get('q') ?? '') !== '' ||
    (savable.get('country') ?? '') !== '' ||
    savable.get('view') === 'blocked'

  /** ⚠️ Which saved search, if any, the list is currently showing. Compared as a
   *  normalised query string rather than field by field. */
  const active = (searches ?? []).find(
    (s) => new URLSearchParams(s.params).toString() === savable.toString(),
  )

  const save = () => {
    void createSavedSearch('contacts', label, savable.toString())
      .then(() => {
        setLabel('')
        setProblem(null)
        reload()
      })
      .catch((e: Error) => setProblem(e.message))
  }

  const remove = (id: string) => {
    void deleteSavedSearch('contacts', id)
      .then(reload)
      .catch((e: Error) => setProblem(e.message))
  }

  return (
    <div className="clist__saved" data-testid="contacts-saved">
      {(searches ?? []).map((s) => (
        <span
          key={s.id}
          className={
            active?.id === s.id ? 'clist__saved-item clist__saved-item--on' : 'clist__saved-item'
          }
        >
          <button
            type="button"
            className="clist__saved-open"
            aria-current={active?.id === s.id ? 'true' : undefined}
            onClick={() => onParamsChange(new URLSearchParams(s.params))}
          >
            {s.label}
          </button>
          {/**
           * ⛔ THE REMOVE CONTROL IS ABSENT RATHER THAN DISABLED WHEN IT IS NOT
           * YOURS. A disabled cross on somebody else's saved view is a control
           * that exists to say no; the server refuses it either way.
           */}
          {s.mine && (
            <button
              type="button"
              className="clist__saved-remove"
              aria-label={`Delete saved search ${s.label}`}
              onClick={() => remove(s.id)}
            >
              ×
            </button>
          )}
        </span>
      ))}

      {/**
       * ⛔ THE SAVE CONTROL APPEARS ONLY WHEN THERE IS SOMETHING TO SAVE, and
       * disappears once this exact search already has a name. Offering "Save
       * Search" over a saved search invites a second copy of it.
       */}
      {narrows && active === undefined && (
        <details className="clist__saved-new" data-testid="contacts-save-search">
          <summary className="clist__saved-trigger">Save Search</summary>
          <div className="clist__saved-panel">
            <label className="clist__saved-label" htmlFor="saved-search-name">
              Name
            </label>
            <input
              id="saved-search-name"
              className="clist__saved-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Indonesian divers"
            />
            {problem !== null && (
              <p className="clist__saved-problem" role="status">
                {problem}
              </p>
            )}
            {/**
             * ⛔ THE DISABLED BUTTON SAYS WHY IT IS DISABLED. Amir's finding 6
             * was partly this: Save is greyed until a name is typed, and
             * **nothing on screen said that a name was what it was waiting
             * for**, so the control read as broken rather than as waiting.
             *
             * ⚠️ A HINT RATHER THAN ENABLING IT AND REFUSING. The server does
             * refuse a nameless search, but a button that is always live and
             * sometimes answers with an error teaches people to ignore errors -
             * which is this project's own argument for the Save control on the
             * column chooser two components down.
             */}
            {label.trim() === '' && (
              <p className="clist__saved-hint" role="status">
                Give it a name to save it.
              </p>
            )}
            <button
              type="button"
              className="clist__saved-save"
              disabled={label.trim() === ''}
              onClick={save}
              data-testid="contacts-save-search-confirm"
            >
              Save
            </button>
          </div>
        </details>
      )}
    </div>
  )
}

/**
 * ⛔ THE COLUMN CHOOSER. Amir 2026-09-12: every list view lets a person choose
 * its columns, fixed fields and custom fields both.
 *
 * ┌─ ⛔ UP AND DOWN BUTTONS RATHER THAN DRAG-AND-DROP, AND IT IS NOT A SHORTCUT ┐
 * │ A drag handle is unreachable by keyboard, announces nothing, and needs a     │
 * │ parallel keyboard mechanism built anyway to be usable at all - so the        │
 * │ "richer" version is this version PLUS a drag layer. It can be added later    │
 * │ over exactly these controls; starting with the drag layer means shipping a   │
 * │ reorder nobody on a keyboard can perform.                                   │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ AND THE DRAFT IS LOCAL UNTIL SAVE, which is the Save/Cancel rule arriving
 * on its first surface: a checkbox that wrote immediately would make "show me
 * what this looks like" a change somebody has to undo.
 */
function ColumnChooser({
  columns,
  onSaved,
}: {
  columns: ResolvedColumn[]
  onSaved: (next: ResolvedColumn[]) => void
}) {
  const [draft, setDraft] = useState<ResolvedColumn[]>(columns)
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  /**
   * ┌─ ⛔ AMIR'S FINDING 1, 2026-09-12: THE POPOVER NEVER CLOSED. ───────────────┐
   * │ Save and Cancel both left it open, so the table it had just changed sat    │
   * │ behind it and the only way out was to click the trigger again.             │
   * │                                                                          │
   * │ ⚠️ AND THE SELF-INSPECTION STEP HAD THE EVIDENCE AND DID NOT USE IT. The   │
   * │ state was driven and captured; **the capture shows an open popover over    │
   * │ the saved table.** Second miss in the same class as the clipped full-page  │
   * │ screenshot: present in the image, not in what I was checking. Asserted now │
   * │ in `list-controls.spec.ts` rather than left to the next reader.            │
   * └──────────────────────────────────────────────────────────────────────────┘
   *
   * ⛔ A REF RATHER THAN A CONTROLLED `open` PROP, because `<details>` is the
   * element doing the opening and closing. Making `open` React state would mean
   * reimplementing the summary click, Escape and the disclosure semantics that
   * are the whole reason this is a `<details>` and not a div.
   */
  const panel = useRef<HTMLDetailsElement | null>(null)
  const close = () => {
    if (panel.current !== null) panel.current.open = false
  }

  /**
   * ⛔ OPENING OR CLOSING THE PANEL RESETS THE DRAFT TO WHAT IS STORED, which
   * makes closing it a Cancel. That is the behaviour rather than an accident of
   * wiring `onToggle`: an unsaved rearrangement that survived a close would be a
   * change somebody made, could not see, and would find again later.
   */
  const reset = () => {
    setDraft(columns)
    setProblem(null)
  }

  const move = (index: number, by: number) => {
    const next = [...draft]
    const target = index + by
    if (target < 0 || target >= next.length) return
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item!)
    setDraft(next)
  }

  const save = () => {
    setSaving(true)
    void saveListColumns('contacts', draft.map((c) => ({ id: c.id, visible: c.visible })))
      .then((result) => {
        setProblem(null)
        onSaved(result.columns)
        /** ⛔ CLOSES ONLY ON SUCCESS. A refused save that closed the panel would
         *  take the person's arrangement and the server's reason off screen
         *  together, which is the partial-save problem one surface over. */
        close()
      })
      .catch((e: Error) => setProblem(e.message))
      .finally(() => setSaving(false))
  }

  const visibleCount = draft.filter((c) => c.visible).length

  return (
    <details ref={panel} className="clist__columns" data-testid="contacts-columns" onToggle={reset}>
      <summary className="clist__columns-trigger">Columns</summary>
      <div className="clist__columns-panel">
        <ul className="clist__columns-list">
          {draft.map((c, index) => (
            <li key={c.id} className="clist__columns-item" data-column-id={c.id}>
              <label className="clist__columns-label">
                <input
                  type="checkbox"
                  checked={c.visible}
                  onChange={(e) => {
                    const next = [...draft]
                    next[index] = { ...c, visible: e.target.checked }
                    setDraft(next)
                  }}
                />
                {c.label}
              </label>
              <span className="clist__columns-move">
                <button
                  type="button"
                  className="clist__columns-step"
                  aria-label={`Move ${c.label} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="clist__columns-step"
                  aria-label={`Move ${c.label} down`}
                  disabled={index === draft.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
              </span>
            </li>
          ))}
        </ul>

        {/**
         * ⛔ THE LAST COLUMN CANNOT BE TURNED OFF, AND THE CONTROL SAYS SO
         * BEFORE THE SERVER DOES. The server refuses an empty set with a 422 -
         * that is the authority - but a person arrives at an empty set one
         * checkbox at a time, and finding out at Save is finding out after the
         * work.
         */}
        {visibleCount === 0 && (
          <p className="clist__columns-problem" role="status">
            A list needs at least one column.
          </p>
        )}
        {problem !== null && (
          <p className="clist__columns-problem" role="status">
            {problem}
          </p>
        )}

        <div className="clist__columns-actions">
          <button
            type="button"
            className="clist__columns-save"
            data-testid="contacts-columns-save"
            disabled={saving || visibleCount === 0}
            onClick={save}
          >
            Save
          </button>
          <button
            type="button"
            className="clist__columns-cancel"
            data-testid="contacts-columns-cancel"
            onClick={close}
          >
            Cancel
          </button>
        </div>
      </div>
    </details>
  )
}

/**
 * ⛔ HOW ONE CELL IS DRAWN, WHICH IS THE HALF THAT STAYS ON THIS SIDE. The
 * server decides WHICH columns and what they are called; this decides what a
 * value looks like, and every arm below is a decision that was already made and
 * is carried across rather than re-made.
 *
 * ⚠️ AN UNKNOWN FIXED KEY RENDERS NOTHING RATHER THAN THROWING, and it is
 * reachable: a server that grows a column this build does not know sends it, and
 * a blank cell is a worse answer than a missing column but a much better one
 * than a list that does not render. **It is also why `list-columns.ts` and this
 * switch are asserted against each other** rather than trusted to agree.
 */
function cellFor(
  column: ResolvedColumn,
  row: ContactListRow,
  currency: string,
  /** ⛔ From `org_settings`, zero for this deployment. Amir 2026-09-12. */
  decimals: number,
  onOpenContact: (reference: number) => void,
) {
  if (column.kind === 'custom') {
    const value = row.customValues[column.id]
    return value === null || value === undefined || value === '' ? (
      <span className="clist__absent">not set</span>
    ) : (
      value
    )
  }
  switch (column.id) {
    case 'fixed:reference':
      /** ⚠️ A BUTTON RATHER THAN TEXT, which is the accessibility half of the
       *  whole-row click: a `<tr>` with an onClick is not keyboard reachable and
       *  is announced as nothing. */
      return (
        <button type="button" className="clist__open" onClick={() => onOpenContact(row.reference)}>
          #{row.reference}
        </button>
      )
    case 'fixed:name':
      return (
        <>
          {[row.firstName, row.lastName].filter(Boolean).join(' ') || row.displayName || (
            <span className="clist__absent">No name</span>
          )}
          {/* ⚠️ The number is beneath the name rather than in its own column: a
              contact has several and the column would have to pick one silently.
              ⛔ And on its OWN LINE, because sharing one truncating line clipped
              it to "+66910002..." - found by reading the screenshot. */}
          <span className="clist__name-number">{row.phone ?? 'no number'}</span>
          {/**
           * ⛔ A BLOCKED ROW SAYS SO, AND WHEN. Found by reading the capture:
           * every row in `?view=blocked` was visually identical to an ordinary
           * one, so the only thing saying what the list held was a toggle above
           * it. ⚠️ It renders whenever `blockedAt` is set rather than only in
           * that view, because a search that matches one will show it too.
           */}
          {row.blockedAt !== null && (
            <span className="clist__blocked" data-testid="row-blocked">
              Blocked {row.blockedAtDisplay}
            </span>
          )}
        </>
      )
    case 'fixed:country':
      /* ⛔ AN EMPTY CELL SAYS NOTHING, which is Amir's finding 8 in a column
         instead of a card: `?? ''` left the reader unable to tell an unknown
         country from a rendering fault. */
      return row.countryName ?? <span className="clist__absent">not set</span>
    case 'fixed:bookings':
      return row.bookingCount
    case 'fixed:value':
      /* ⛔ NULL is impossible - value_total is NOT NULL DEFAULT 0 - so a bare
         number is honest. ⛔ But it is not unitless: the account's currency is
         what makes 3900.00 a quantity of something. */
      return formatMoney(row.valueTotal, currency, decimals)
    case 'fixed:created':
      return row.createdAtDisplay
    case 'fixed:email':
      return row.email ?? <span className="clist__absent">not set</span>
    case 'fixed:assigned':
      /** ⚠️ "Unassigned" rather than "not set": a contact with no responsible
       *  user is a state somebody acts on, not a blank field. */
      return row.assignedName ?? <span className="clist__absent">Unassigned</span>
    case 'fixed:language':
      return row.language ?? <span className="clist__absent">not set</span>
    case 'fixed:blocked':
      return row.blockedAt === null ? (
        <span className="clist__absent">No</span>
      ) : (
        row.blockedAtDisplay
      )
    default:
      return null
  }
}

type Props = {
  onOpenContact: (reference: number) => void
  /**
   * ⛔ THE SORT, FILTER AND VIEW COME FROM THE URL. Found by the self-inspection
   * step: three captures of different states were **byte-identical**, because
   * this component held them in `useState` and ignored the query string, so
   * `/contacts?sort=bookings` rendered the default list.
   *
   * ⚠️ AND IT IS THE SAME ARGUMENT THE SEARCH PAGE ALREADY MADE: the query lives
   * in the URL so a search can be linked and re-run. A filtered list nobody can
   * send to a colleague is a filter that exists only for the person who typed
   * it, and §13's saved views are that idea made permanent.
   */
  params: URLSearchParams
  onParamsChange: (next: URLSearchParams) => void
}

export function ContactsList({ onOpenContact, params, onParamsChange }: Props) {
  /**
   * ⚠️ THE SORT IS NOT VALIDATED HERE ANY MORE, AND THAT REMOVES A SECOND
   * VOCABULARY RATHER THAN A CHECK. This read `COLUMNS.some(...)` against a
   * literal that no longer exists; `parseSort` on the server already falls back
   * to `created` for anything it does not know, so validating here was one more
   * place that had to agree about which sorts exist.
   */
  const sort = params.get('sort') ?? 'created'
  const dir = params.get('dir') === 'asc' ? 'asc' : 'desc'
  const query = params.get('q') ?? ''
  const country = params.get('country') ?? ''
  const view = params.get('view') === 'blocked' ? 'blocked' : 'active'
  /**
   * ⛔ THE PAGE AND THE ROWS-PER-PAGE LIVE IN THE URL like everything else here,
   * for the reason the sort does: a page somebody is looking at can be sent to a
   * colleague. **"Page 4 of the Indonesian contacts sorted by value" is a thing
   * one person tells another**, and infinite scroll could not express it.
   */
  const pageNumber = Math.max(1, Math.floor(Number(params.get('page')) || 1))
  const rowsPerPage = parseRowsPerPage(params.get('rows') ?? undefined)

  /**
   * ⛔ EVERY PARAMETER THIS LIST HONOURS, NAMED, SO THE ONES IT DOES NOT CAN BE
   * SAID OUT LOUD. Amir opened `/contacts?country=ID` before the filter existed
   * and got all 2,987 contacts with nothing saying the parameter had been
   * ignored. **An unrecognised parameter that changes nothing is indistinguishable
   * from a filter that matched everything**, and the second is an answer somebody
   * acts on.
   *
   * ⚠️ AND IT IS A LIST RATHER THAN A CHECK ON `country` ALONE, because the next
   * parameter somebody invents will have the same problem. The failure was not
   * about countries.
   */
  const HONOURED = ['sort', 'dir', 'q', 'country', 'view', 'page', 'rows']
  const ignored = [...params.keys()].filter((k) => !HONOURED.includes(k))

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value === null || value === '') next.delete(key)
    else next.set(key, value)
    onParamsChange(next)
  }
  const setQuery = (q: string) => setParam('q', q)
  const setView = (v: 'active' | 'blocked') => setParam('view', v === 'blocked' ? 'blocked' : null)

  const [rows, setRows] = useState<ContactListRow[] | null>(null)
  const [page, setPage] = useState<ContactsPageResponse | null>(null)

  /**
   * ⛔ THE ACCOUNT LABELS ARE REMEMBERED ACROSS A RELOAD, and `page` is reset on
   * every sort change, which is why they are not read from it. Taking the header
   * from `page` would blank the value column each time somebody sorted, and
   * defaulting to "Value" in the gap would put the hardcoded label back on
   * screen for exactly as long as anyone notices it.
   *
   * ⚠️ SO THE TABLE WAITS FOR THE FIRST PAGE and the skeleton covers it. An
   * account setting does not change between two requests, so once these are
   * known they stay known.
   */
  const [labels, setLabels] = useState<{
    valueLabel: string
    currency: string
    decimalPlaces: number
  } | null>(null)
  const currency = labels?.currency ?? ''
  /** ⛔ From org_settings, and 2 is Intl's own default rather than a value this
   *  file chose: it applies for the one render before the first page arrives. */
  const decimals = labels?.decimalPlaces ?? 2

  /**
   * ⛔ THE COLUMN SET IS HELD ACROSS FETCHES FOR THE REASON THE LABELS ARE.
   * `page` is cleared on every sort, filter and page change, so reading the
   * columns off it would collapse the table to nothing between two requests and
   * rebuild it - a whole table flickering because somebody clicked a header.
   */
  const [columns, setColumns] = useState<ResolvedColumn[] | null>(null)
  const shown = (columns ?? []).filter((c) => c.visible)
  /**
   * ⚠️ A COUNTER RATHER THAN A BOOLEAN, so two saves in a row both refetch. A
   * flag set to `true` twice is one change, and the second save would draw the
   * first save's rows.
   */
  const [reloadToken, setReloadToken] = useState(0)

  /**
   * ⛔ THE TOTAL IS HELD SEPARATELY FROM `page`, because only the FIRST page
   * carries it. Paging does not change how many rows match, so the server counts
   * once per filter and returns `null` afterwards - reading it off `page` would
   * make the number vanish the moment somebody scrolled.
   */
  const [counted, setCounted] = useState<{ total: number; capped: boolean } | null>(null)
  const total = counted?.total ?? null
  const totalIsCapped = counted?.capped ?? false

  /**
   * ⚠️ EVERY CHANGE OF SORT, FILTER OR VIEW RESETS THE ROWS, and it has to: the
   * cursor encodes a position in ONE ordering, so appending a page fetched under
   * a new sort to rows fetched under the old one produces a list that is in no
   * order at all.
   */
  useEffect(() => {
    let current = true
    setRows(null)
    setPage(null)
    setCounted(null)
    void fetchContactsPage({ sort, dir, q: query, country, view, page: pageNumber, rows: rowsPerPage })
      .then((p) => {
        if (!current) return
        setRows(p.rows)
        setPage(p)
        setLabels({ valueLabel: p.valueLabel, currency: p.currency, decimalPlaces: p.decimalPlaces })
        setColumns(p.columns)
        if (p.total !== null) setCounted({ total: p.total, capped: p.totalIsCapped })
      })
      .catch(() => {
        if (current) setRows([])
      })
    return () => {
      current = false
    }
  }, [sort, dir, query, country, view, pageNumber, rowsPerPage, reloadToken])

  /**
   * ┌─ ⛔ NUMBERED PAGES REPLACED INFINITE SCROLL. Amir 2026-09-12. ─────────────┐
   * │ He wants a footer that can hold things, and infinite scroll forecloses     │
   * │ one - there is no bottom to put a footer at.                              │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⛔ SO `loadMore`, THE INTERSECTION OBSERVER AND THE SENTINEL ARE GONE, and
   * the rows are REPLACED on every fetch rather than appended. Appending was the
   * whole reason the observer existed; a page is a page.
   *
   * ⚠️ AND THE PANE STILL SCROLLS ITSELF. That was never about the observer: the
   * layout puts a 100dvh ceiling above it, and a pane that grew the document
   * would make the page scroll, which is the defect the layout suite exists for.
   */
  const pane = useRef<HTMLDivElement | null>(null)

  /**
   * ⚠️ ONE PARAMETER CHANGE PER CLICK, which is why this writes both at once
   * rather than calling two setters: each writes the whole URL, so the second
   * would overwrite the first's.
   */
  const toggleSort = (key: string) => {
    const next = new URLSearchParams(params)
    if (key === sort) {
      if (dir === 'desc') next.set('dir', 'asc')
      else next.delete('dir')
    } else {
      next.set('sort', key)
      next.delete('dir')
    }
    onParamsChange(next)
  }

  return (
    <main className="clist" data-testid="contacts-list">
      <header className="clist__bar">
        {/* ⚠️ A FILTER BAR, per §13's one navigation rule: saved views live in a
            filter bar and the sub nav holds page sections. This is the bar, and
            since 2026-09-12 the saved views are in it - see `SavedSearches`. */}
        <input
          className="clist__search"
          type="search"
          placeholder="Search name, number or reference"
          aria-label="Search contacts"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="contacts-search"
        />
        <div className="clist__views">
          <button
            type="button"
            className={view === 'active' ? 'clist__view clist__view--on' : 'clist__view'}
            aria-pressed={view === 'active'}
            onClick={() => setView('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={view === 'blocked' ? 'clist__view clist__view--on' : 'clist__view'}
            aria-pressed={view === 'blocked'}
            onClick={() => setView('blocked')}
            data-testid="contacts-view-blocked"
          >
            Blocked
          </button>
        </div>

        {/**
         * ⛔ SAVED SEARCHES LIVE IN THE FILTER BAR, per §13's one navigation
         * rule: *"saved views live in a filter bar and the sub nav holds page
         * sections"*. This comment used to say the saved views that belong here
         * are item 13's segments, in the future tense. They are, and they are
         * here.
         */}
        <SavedSearches params={params} onParamsChange={onParamsChange} />

        {columns !== null && (
          <ColumnChooser
            columns={columns}
            onSaved={(next) => {
              setColumns(next)
              /** ⚠️ A REFETCH, BECAUSE A NEWLY VISIBLE CUSTOM COLUMN HAS NO
               *  VALUES IN THE ROWS ALREADY ON SCREEN. The server only selects
               *  the custom columns that are on, so turning one on without
               *  refetching would draw an empty column over real contacts. */
              setRows(null)
              setReloadToken((n) => n + 1)
            }}
          />
        )}
      </header>

      {/**
       * ⛔ THE HIDDEN COUNT, WHICH IS THE HALF §4.6's ARGUMENT REQUIRES. Blocked
       * contacts are absent by a decision somebody made, and a list that silently
       * omits them is the surface that lies by omission.
       */}
      {/**
       * ⛔ THE TOTAL, PER AMIR'S RULING B: every result set states its total.
       * Narrowing 3,000 contacts to twelve with no number is the reader doing a
       * count the system already could.
       *
       * ⛔ AND IT IS ABSENT AT ZERO RATHER THAN READING "0 CONTACTS", which is
       * the six-instance finding again. "0" would stand in for three different
       * facts - nothing exists, nothing matched, nothing is visible to you - and
       * the empty-state sentence below already distinguishes them. A number that
       * collapses three facts is worse than no number.
       *
       * ⚠️ "10,000+" WHEN THE COUNT HIT ITS CAP. The cap is stated rather than
       * rounded away: a floor presented as a total is a number that is wrong.
       */}
      {/**
       * ⛔ WHAT THE LIST IS FILTERED BY, SAID OUT LOUD. Amir opened
       * `/contacts?country=ID`, got every contact back, and reported a broken
       * filter; there was no filter. **The list answered a request it had not
       * honoured and looked as though it had**, which is
       * `a-surface-says-nothing-about-what-it-lacks` reaching the query string.
       *
       * ⛔ AND AN UNRECOGNISED CODE IS ITS OWN SENTENCE. "No contacts match" for
       * France and for a typo are the same empty list, and only the server knows
       * which - so it says which.
       */}
      {page?.appliedCountry != null && (
        <p className="clist__note" data-testid="contacts-country-filter">
          {page.appliedCountry.name === null ? (
            <>
              <strong>{page.appliedCountry.code}</strong> is not a country code this system knows,
              so nothing matches it.{' '}
            </>
          ) : (
            <>
              Filtered to <strong>{page.appliedCountry.name}</strong>.{' '}
            </>
          )}
          <button
            type="button"
            className="clist__link"
            onClick={() => setParam('country', null)}
            data-testid="contacts-clear-country"
          >
            Clear Filter
          </button>
        </p>
      )}

      {/**
       * ⚠️ AND A PARAMETER THIS LIST DOES NOT HONOUR IS NAMED RATHER THAN
       * DROPPED. The next invented parameter has the same problem as `country`
       * did, and the failure was never about countries.
       */}
      {ignored.length > 0 && (
        <p className="clist__note" data-testid="contacts-ignored-params">
          This list does not filter by {ignored.join(', ')}, so {ignored.length === 1 ? 'it' : 'they'}{' '}
          had no effect.
        </p>
      )}

      {total !== null && total > 0 && (
        <p className="clist__total" data-testid="contacts-total">
          {COUNT_FORMAT.format(total)}
          {totalIsCapped && '+'} {total === 1 && !totalIsCapped ? 'contact' : 'contacts'}
        </p>
      )}

      {view === 'active' && (page?.blockedHidden ?? 0) > 0 && (
        <p className="clist__note" data-testid="contacts-blocked-note">
          {page!.blockedHidden} blocked {page!.blockedHidden === 1 ? 'contact is' : 'contacts are'}{' '}
          hidden.{' '}
          <button type="button" className="clist__link" onClick={() => setView('blocked')}>
            Show them
          </button>
        </p>
      )}

      <div className="clist__pane" ref={pane}>
        {columns !== null && (
        <table className="clist__table">
          <thead>
            <tr>
              {/**
               * ⛔ `aria-sort` ON THE `th`, WHICH IS WHERE IT BELONGS AND WHERE IT
               * WAS NOT. The first version put this reasoning in a comment and
               * then set `aria-label` on the button - **a comment describing
               * something the code did not do**, which is the shape this project
               * keeps recording. Caught by reading the capture and then reading
               * the markup that produced it.
               *
               * ⚠️ The arrow is `aria-hidden`: it says the same thing visually,
               * and read aloud it is a character nobody wants.
               */}
              {/**
                * ⛔ AND A COLUMN THAT CANNOT SORT GETS TEXT, NOT A DEAD BUTTON.
                * A custom field's value arrives as its DISPLAY string, and
                * sorting display strings puts 9 after 10 - the exact defect §4.7
                * rejected a single `value text` column for. So the server sends
                * `sort: null` for those, and this renders a header rather than a
                * control that would sort wrongly or not at all.
                */}
              {shown.map((c) => (
                <th
                  key={c.id}
                  scope="col"
                  data-column-id={c.id}
                  aria-sort={
                    c.sort !== null && sort === c.sort
                      ? dir === 'desc'
                        ? 'descending'
                        : 'ascending'
                      : 'none'
                  }
                >
                  {c.sort === null ? (
                    <span className="clist__header">{c.label}</span>
                  ) : (
                    <button
                      type="button"
                      className="clist__sort"
                      onClick={() => toggleSort(c.sort!)}
                      data-testid={`sort-${c.sort}`}
                      aria-label={`Sort by ${c.label}`}
                    >
                      {c.label}
                      {sort === c.sort && (
                        <span aria-hidden="true"> {dir === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </button>
                  )}
                </th>
              ))}
              {/* ⚠️ The actions column has no label and no sort: it is a control
                  rather than a fact about the contact. */}
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows?.map((r) => (
              /**
               * ⛔ THE WHOLE ROW OPENS THE CONTACT. Amir 2026-09-12. The
               * reference alone was a small target in a wide row, and every row
               * in this table does exactly one thing.
               *
               * ⚠️ AND THE REFERENCE STAYS A BUTTON RATHER THAN BECOMING TEXT,
               * which is the accessibility half: a `<tr>` with an onClick is
               * not reachable by keyboard and is announced as nothing. The
               * button is what a screen reader and the Tab key find; the row
               * handler is a convenience for a mouse.
               *
               * ⛔ AND THE MENU STOPS PROPAGATION, or opening it would open the
               * contact underneath it.
               */
              <tr
                key={r.id}
                className="clist__row"
                data-testid="contact-row"
                data-contact-reference={r.reference}
                onClick={() => onOpenContact(r.reference)}
              >
                {shown.map((c) => (
                  <td key={c.id} className={c.id === 'fixed:name' ? 'clist__name' : undefined}>
                    {cellFor(c, r, currency, decimals, onOpenContact)}
                  </td>
                ))}
                {/**
                 * ┌─ ⛔ ROW ACTIONS. AMIR'S SECOND INSPECTION: THE MENU HAD THE ─┐
                 * │   WRONG ACTION.                                             │
                 * │ It offered "Open Contact", **which the whole row already     │
                 * │ does** - so the menu's only entry duplicated the thing it    │
                 * │ sat inside. A menu with one redundant action is worse than   │
                 * │ no menu: it costs a click to discover it offers nothing.     │
                 * │                                                            │
                 * │ ⚠️ AND MY OWN REASONING GOT IT HALF RIGHT. I refused four    │
                 * │ dead controls, correctly, and then shipped the one live      │
                 * │ control **without asking whether it was worth an entry.**    │
                 * │ "Does this action exist" and "does this action add anything  │
                 * │ here" are different questions and I only asked the first.    │
                 * └────────────────────────────────────────────────────────────┘
                 *
                 * ⛔ WHAT AMIR ASKED FOR: view details, archive contact, block.
                 * **Two of the three ship.** *View Details* stays because a menu
                 * needs a way to say what the row does; *Block* is item 14 and
                 * had no write path in the entire product until today.
                 *
                 * ⛔ *ARCHIVE* IS DROPPED, RULED BY AMIR 2026-09-12, AND THE
                 * MENU IS FINAL AT TWO ACTIONS. Three different features answer
                 * to the word - contact-level soft delete, hide-from-lists, and
                 * closing every conversation - and **hide-from-lists is the only
                 * one that matches it, which is close enough to Block that the
                 * product would carry two controls nobody could distinguish.**
                 *
                 * ⚠️ A REJECTION RATHER THAN A DEFERRAL: no trigger, no later
                 * phase, nothing inherits it. The three shapes are named in
                 * §3.10 so this does not come back as an obvious omission.
                 */}
                <td className="clist__actions">
                  <details
                    className="clist__menu"
                    data-testid="contact-row-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <summary className="clist__menu-trigger" aria-label={`Actions for #${r.reference}`}>
                      {/* ⚠️ The literal character rather than a numeric HTML
                          entity: an entity of the form hash-plus-digits reads as
                          a hex colour to check-colors, which is the same false
                          positive a booking reference produced in August. And
                          this comment cannot spell the entity either, for the
                          same reason - which is the check being consistent
                          rather than a second defect. */}
                      ⋯
                    </summary>
                    <div className="clist__menu-items">
                      <button
                        type="button"
                        className="clist__menu-item"
                        onClick={() => onOpenContact(r.reference)}
                      >
                        View Details
                      </button>
                      {/**
                       * ⛔ THE LABEL SAYS WHICH DIRECTION IT GOES, because the
                       * blocked view shows blocked rows and a menu reading
                       * "Block" on one of them would be a control whose effect
                       * nobody can predict.
                       */}
                      <button
                        type="button"
                        className="clist__menu-item"
                        data-testid="contact-row-block"
                        onClick={() => {
                          void setContactBlocked(r.reference, r.blockedAt === null)
                            .then(() => setReloadToken((n) => n + 1))
                            .catch(() => undefined)
                        }}
                      >
                        {r.blockedAt === null ? 'Block Contact' : 'Unblock Contact'}
                      </button>
                    </div>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}

        {rows === null && (
          <div className="clist__skeletons" data-testid="contacts-loading">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="record__skeleton" />
            ))}
          </div>
        )}

        {/**
         * ⛔ THE THREE ABSENCES, EACH WITH ITS OWN SENTENCE. The server decides
         * which; this renders it. Deciding here would mean the client
         * re-deriving "is a filter applied" from state it does not fully hold -
         * it cannot see the scope at all.
         */}
        {rows !== null && rows.length === 0 && (
          <p className="clist__empty" data-testid="contacts-empty">
            {page?.emptiness === 'no-contacts-at-all'
              ? 'No contacts yet. They arrive when somebody messages you, or from an import.'
              : page?.emptiness === 'none-visible'
                ? 'No contacts you can see. Others exist and are assigned to colleagues.'
                : view === 'blocked'
                  ? 'No blocked contacts.'
                  : /**
                     * ⛔ THE COUNTRY FILTER GETS ITS OWN SENTENCE, and reading the
                     * capture of the state I had just added is what found it: the
                     * QQ view said the code was unknown at the top and "No contacts
                     * match this search" at the bottom, **with no search**. Two
                     * sentences about one empty list, one of them naming a cause
                     * that was not the cause.
                     *
                     * ⚠️ WHICH IS THE SIX-INSTANCE FINDING ARRIVING IN THE FIX FOR
                     * THE SIX-INSTANCE FINDING. Adding a filter adds an absence,
                     * and an absence needs its own sentence or it borrows one.
                     */
                    page?.appliedCountry != null && query === ''
                    ? page.appliedCountry.name === null
                      ? 'No contacts, because that country code matches nothing.'
                      : `No contacts in ${page.appliedCountry.name}.`
                    : 'No contacts match this search.'}
            {/**
             * ⛔ AND A WAY BACK OUT, which the blocked note already offered and
             * this did not. Found by reading the capture: a search matching
             * nothing left the query in the box and no way to leave it except
             * selecting the text by hand.
             */}
            {page?.emptiness === 'no-matches' && query !== '' && (
              <>
                {' '}
                <button
                  type="button"
                  className="clist__link"
                  onClick={() => setQuery('')}
                  data-testid="contacts-clear-search"
                >
                  Clear Search
                </button>
              </>
            )}
          </p>
        )}

      </div>

      {/**
       * ┌─ ⛔ THE PAGER, WHICH IS THE FOOTER AMIR WANTED A PLACE FOR ─────────────┐
       * │ Infinite scroll foreclosed one: there is no bottom to put a footer at.  │
       * └────────────────────────────────────────────────────────────────────────┘
       *
       * ⛔ IT RENDERS EVEN AT ONE PAGE, and the rows-per-page control is why: a
       * footer that disappeared below 51 contacts would take the setting with it,
       * and the setting is what a person uses to see MORE at once. **The page
       * buttons are what hide, not the footer.**
       *
       * ⚠️ AND "OF 150+" WHEN THE COUNT IS CAPPED. `pageCount` is a floor then,
       * and a pager claiming an exact last page it cannot reach would be a
       * control that lies about where it ends.
       */}
      {page !== null && (
        <nav className="clist__pager" aria-label="Pages" data-testid="contacts-pager">
          <div className="clist__pager-rows">
            <label htmlFor="rows-per-page">Rows</label>{' '}
            <select
              id="rows-per-page"
              value={rowsPerPage}
              data-testid="contacts-rows-per-page"
              onChange={(e) => {
                /**
                 * ⛔ CHANGING THE SIZE RETURNS TO PAGE 1, because page 7 of 25 is
                 * not page 7 of 100 and keeping the number would jump somebody
                 * somewhere they did not ask to go. Both parameters move in ONE
                 * write, or the second overwrites the first.
                 */
                const next = new URLSearchParams(params)
                next.set('rows', e.target.value)
                next.delete('page')
                onParamsChange(next)
              }}
            >
              {ROWS_PER_PAGE.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {page.pageCount > 1 && (
            <div className="clist__pager-pages">
              <button
                type="button"
                className="clist__pager-step"
                disabled={pageNumber <= 1}
                data-testid="contacts-prev"
                onClick={() => setParam('page', pageNumber <= 2 ? null : String(pageNumber - 1))}
              >
                Previous
              </button>
              <span className="clist__pager-position" data-testid="contacts-page-position">
                Page {pageNumber} of {COUNT_FORMAT.format(page.pageCount)}
                {totalIsCapped && '+'}
              </span>
              <button
                type="button"
                className="clist__pager-step"
                disabled={pageNumber >= page.pageCount}
                data-testid="contacts-next"
                onClick={() => setParam('page', String(pageNumber + 1))}
              >
                Next
              </button>
            </div>
          )}
        </nav>
      )}
    </main>
  )
}
